import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Button from "./Button";
import ListWrapper from "../utils/ListWrapper";
import ScrollView2 from "./ScrollView2";
import D3EAppTemplate from "../models/D3EAppTemplate";
import TextView from "./TextView";
import { BuildContext } from "../classes/BuildContext";

type _BuildTemplateListViewOnClearSelection = (
  items: Array<D3EAppTemplate>
) => void;

type _Button5OnPressed = (d3eState: BuildTemplateListViewRefs) => void;

export interface BuildTemplateListViewProps extends BaseUIProps {
  key?: string;
  templates?: Array<D3EAppTemplate>;
  _templatesHash?: number;
  onClearSelection?: _BuildTemplateListViewOnClearSelection;
}
/// To store state data for BuildTemplateListView
class BuildTemplateListViewRefs {
  public button5: Button5State = new Button5State();
  wrapScrollController: ui.ScrollController = new ui.ScrollController();
}

interface Button5WithStateProps extends BaseUIProps {
  key?: string;
  d3eState: BuildTemplateListViewRefs;
  _clearSelectionHandler?: _Button5OnPressed;
}

class Button5State extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _Button5WithState extends ObservableComponent<Button5WithStateProps> {
  button5FocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: Button5WithStateProps) {
    super(props);

    this.initState();
  }
  public get button5(): Button5State {
    return this.props.d3eState.button5;
  }
  public get d3eState(): BuildTemplateListViewRefs {
    return this.props.d3eState;
  }
  public get _clearSelectionHandler(): _Button5OnPressed {
    return this.props._clearSelectionHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("button5", null, this.button5);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["button5", "button5.", "button5.hover"], this.rebuild);
  }
  public button5OnEnter(event): void {
    return this.button5.setHover(true);
  }
  public button5OnExit(event): void {
    return this.button5.setHover(false);
  }
  public dispose(): void {
    this.button5.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.symmetric({
        horizontal: 0.0,
        vertical: 10.0,
        transitions: new Map(),
      }),
      child: Button({
        decoration: cStyle.tButtonBtnLinkDecorationOn,
        disable: this.button5.disable,
        onPressed: () => {
          this._clearSelectionHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Clear Selection", className: "vc" }),
        onEnter: (event) => {
          this.button5OnEnter(event);
        },
        onExit: (event) => {
          this.button5OnExit(event);
        },
      }),
      className: "x15 vc",
    });
  }
}
function Button5WithState(props: Button5WithStateProps) {
  return React.createElement(_Button5WithState, props);
}

class _BuildTemplateListViewState extends ObservableComponent<BuildTemplateListViewProps> {
  static defaultProps = { templates: [], onClearSelection: null };
  d3eState: BuildTemplateListViewRefs = new BuildTemplateListViewRefs();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: BuildTemplateListViewProps) {
    super(props);

    this.initState();
  }
  public get templates(): Array<D3EAppTemplate> {
    return this.props.templates;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.subscribeToList(this.templates, "templates");

    this.updateSyncCollProperty("templates", this.props.templates);

    this.on(["templates", "templates.icon"], this.rebuild);
  }
  public componentDidUpdate(prevProps: BuildTemplateListViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.templates !== this.props.templates) {
      this.updateObservableColl(
        "templates",
        prevProps.templates,
        this.props.templates
      );

      this.fire("templates", this);
    }
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ScrollView2({
      child: ui.Wrap({
        crossAxisAlignment: ui.WrapCrossAlignment.center,
        spacing: 10,
        runSpacing: 10,
        children: [
          this.templates.expand((item) => [
            ui.NetworkImage({
              url: item.icon?.downloadUrl ?? "",
              height: 60,
              width: 60,
              className: "x4b9 hc vc",
              key: item?.ident,
            }),
          ]),
          Button5WithState({
            d3eState: this.d3eState,
            _clearSelectionHandler: this.clearSelectionHandler,
            key: "1",
          }),
        ],
        className: ui.join(
          this.props.className,
          "BuildTemplateListView x06 vc"
        ),
        ...copyBaseUIProps(this.props),
      }),
      scrollDirection: ui.Axis.vertical,
      className: "BuildTemplateListView x06 vc",
      controller: this.d3eState.wrapScrollController,
    });
  }
  public clearSelectionHandler = (
    d3eState: BuildTemplateListViewRefs
  ): void => {
    this.onClearSelection(this.templates);
  };
  public get onClearSelection(): _BuildTemplateListViewOnClearSelection {
    return this.props.onClearSelection;
  }
  public get button5() {
    return this.d3eState.button5;
  }
}
export default function BuildTemplateListView(
  props: BuildTemplateListViewProps
) {
  return React.createElement(
    _BuildTemplateListViewState,
    { ..._BuildTemplateListViewState.defaultProps, ...props },
    ListWrapper.fromInput<D3EAppTemplate>(props.templates, "templates")
  );
}
